<template lang="pug">
aiq-dialog.group-settings-modal(
    :title="isEdit ? $t(`${baseTranslationPath}.group_settings_title`) : $t(`${baseTranslationPath}.add_group`)"
    :model-value="modelValue"
    :show-close="true"
    :close-on-click-modal="false"
    width="480px"
    @update:model-value="close"
)
    .g-form
        .g-row
            label {{ $t(`${baseTranslationPath}.group_label`) }} *
            aiq-input(
                v-model="form.label"
                :placeholder="$t(`${baseTranslationPath}.group_label_ph`)"
            )
        .g-row
            label {{ $t(`${baseTranslationPath}.group_type`) }}
            aiq-select(v-model="form.group_type" style="width: 100%" clearable filterable)
                aiq-option(v-for="g in groupTypes" :key="g" :label="g" :value="g")
        .g-row
            label {{ $t(`${baseTranslationPath}.semantic_class`) }}
            aiq-select(v-model="form.semantic_class" style="width: 100%" clearable filterable)
                aiq-option(v-for="s in semanticClasses" :key="s" :label="s" :value="s")
            .g-hint(v-if="form.semantic_class === 'transaction'") {{ $t(`${baseTranslationPath}.semantic_class_transaction_note`) }}
        .g-row
            label {{ $t(`${baseTranslationPath}.interaction_class`) }}
            aiq-select(v-model="form.interaction_class" style="width: 100%" clearable filterable)
                aiq-option(v-for="c in interactionClasses" :key="c" :label="c" :value="c")
        .g-row
            label {{ $t(`${baseTranslationPath}.group_member_fields`) }}
            aiq-select(
                v-model="form.field_ids"
                style="width: 100%"
                multiple
                filterable
                :reserve-keyword="false"
                :placeholder="$t(`${baseTranslationPath}.group_member_fields_ph`)"
            )
                aiq-option(
                    v-for="f in fields"
                    :key="f.field_key"
                    :label="fieldLabel(f)"
                    :value="f.field_key"
                )
        .g-row(v-if="isEdit")
            aiq-button(
                v-if="isQuestionGroup"
                plain
                disabled
            )
                font-awesome-icon.btn-check(:icon="['fas', 'check']")
                span {{ $t(`${baseTranslationPath}.group_questions_done`) }}
            aiq-button(
                v-else
                plain
                :loading="generatingQuestions"
                :disabled="!form.field_ids.length"
                @click="createQuestionsFromFields"
            ) {{ $t(`${baseTranslationPath}.group_create_questions`) }}
            .g-hint {{ $t(`${baseTranslationPath}.group_create_questions_hint`) }}
        .actions
            aiq-button.delete-btn(v-if="isEdit" type="danger" plain :loading="deleting" @click="onDelete") {{ $t(`${baseTranslationPath}.delete_group`) }}
            .actions-right
                aiq-button(@click="close") {{ $t('global.cancel') }}
                aiq-button(type="primary" :loading="saving" :disabled="!canSubmit" @click="submit") {{ $t('global.save') }}
</template>
<script>
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import { fieldOptionLabel } from './fieldUtil';

const INTERACTION_CLASSES = ['simple_input', 'long_text', 'boolean_choice', 'single_choice',
  'multi_choice', 'signature', 'computed', 'internal_only', 'repeating_row', 'repeating_group',
  'composite_group', 'conditional_detail'];
// Only the group kinds that drive behavior are offered for manual creation/edit:
// 'table' (repeating rows) and 'other' (single instance). gen-ai's structural types
// (radio_group/checkbox_set/address/date_split) are extraction-only, not hand-built.
const GROUP_TYPES = ['other', 'table'];
// 'transaction' drives the transaction fetch dialog; 'other' is a neutral grouping.
// The full SemanticClass set is extraction-only and never hand-authored here.
const SEMANTIC_CLASSES = ['transaction', 'other'];

export default {
  name: 'GroupSettingsDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    formId: {
      type: [String, Number],
      default: null,
    },
    // the form_groups row being edited, or null when creating.
    group: {
      type: Object,
      default: null,
    },
    // all form_fields rows for the form (membership options).
    fields: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'saved', 'deleted'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
      interactionClasses: INTERACTION_CLASSES,
      groupTypes: GROUP_TYPES,
      semanticClasses: SEMANTIC_CLASSES,
      form: this.emptyForm(),
      saving: false,
      deleting: false,
      generatingQuestions: false,
    };
  },
  computed: {
    isEdit() {
      return !!get(this.group, 'id');
    },
    // the group's current member field keys (enriched GET sends field_ids).
    currentFieldIds() {
      return [...(get(this.group, 'field_ids', []) || [])];
    },
    // Backend flag: true once this group's fields have been converted to
    // questions. The backend clears it on any membership change, so unsaved edits
    // to the member fields already invalidate it — treat those as not-converted
    // so the convert button comes back without waiting for a round trip.
    isQuestionGroup() {
      return !!get(this.group, 'is_question') && !this.membersChanged;
    },
    membersChanged() {
      return !isEqual([...this.form.field_ids].sort(), [...this.currentFieldIds].sort());
    },
    canSubmit() {
      return !!(this.form.label && this.form.label.trim());
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.seed();
      }
    },
    group() {
      if (this.modelValue) {
        this.seed();
      }
    },
  },
  methods: {
    emptyForm() {
      return {
        label: '',
        group_type: 'other',
        semantic_class: 'transaction',
        interaction_class: 'composite_group',
        field_ids: [],
      };
    },
    seed() {
      if (this.isEdit) {
        this.form = {
          label: get(this.group, 'label', '') || '',
          group_type: get(this.group, 'group_type') || null,
          semantic_class: get(this.group, 'semantic_class') || '',
          interaction_class: get(this.group, 'interaction_class') || null,
          field_ids: [...this.currentFieldIds],
        };
      } else {
        this.form = this.emptyForm();
      }
    },
    fieldLabel: fieldOptionLabel,
    close() {
      this.$emit('update:modelValue', false);
    },
    // On edit only send changed attributes, so a label tweak doesn't reshuffle
    // membership and vice versa.
    buildChanges() {
      const body = {};
      const label = this.form.label.trim();
      if (label !== (get(this.group, 'label', '') || '')) {
        body.label = label;
      }
      if ((this.form.group_type || null) !== (get(this.group, 'group_type') || null)) {
        body.group_type = this.form.group_type || null;
      }
      if ((this.form.semantic_class || '') !== (get(this.group, 'semantic_class') || '')) {
        body.semantic_class = this.form.semantic_class || null;
      }
      if ((this.form.interaction_class || null) !== (get(this.group, 'interaction_class') || null)) {
        body.interaction_class = this.form.interaction_class || null;
      }
      if (this.membersChanged) {
        body.field_ids = this.form.field_ids;
      }
      return body;
    },
    async submit() {
      if (!this.canSubmit || !this.formId) {
        return;
      }
      this.saving = true;
      try {
        const saved = this.isEdit ? await this.update() : await this.create();
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.group_saved`));
        this.$emit('saved', saved);
        this.close();
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.group_save_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.saving = false;
      }
    },
    async create() {
      const resp = await this.$store.dispatch('agentForms/addFormGroup', {
        formId: this.formId,
        label: this.form.label.trim(),
        group_type: this.form.group_type || undefined,
        semantic_class: this.form.semantic_class || undefined,
        interaction_class: this.form.interaction_class || undefined,
        field_ids: this.form.field_ids,
      });
      return get(resp, 'data', null);
    },
    async update() {
      const body = this.buildChanges();
      if (!Object.keys(body).length) {
        return this.group;
      }
      const resp = await this.$store.dispatch('agentForms/updateFormGroup', {
        formId: this.formId,
        groupId: this.group.id,
        ...body,
      });
      return get(resp, 'data', this.group);
    },
    // Generate one question per member field via the group questions route. The
    // backend reads the group's member fields, creates a question for each
    // unmapped one, and links them (fields already answered are skipped).
    async createQuestionsFromFields() {
      if (!this.formId || !this.isEdit) {
        return;
      }
      this.generatingQuestions = true;
      try {
        // The route reads the group's SAVED members, so flush pending edits first
        // or the questions would be built from the membership on screen.
        await this.update();
        const resp = await this.$store.dispatch('agentForms/createGroupQuestions', {
          formId: this.formId,
          groupId: this.group.id,
        });
        // 201 returns the created question array; 200 means the fields were
        // already mapped. Both now flag the group is_question: true, so treat
        // both as success — reload (to pick up the flag) and flip the button.
        const created = Array.isArray(get(resp, 'data')) ? resp.data : [];
        if (created.length) {
          this.$aiq.notify.success(
            this.$t(`${this.baseTranslationPath}.group_questions_created`, { count: created.length }),
          );
        } else {
          this.$aiq.notify.info(this.$t(`${this.baseTranslationPath}.group_questions_created_none`));
        }
        this.$emit('saved', this.group);
        this.close();
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.group_questions_create_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.generatingQuestions = false;
      }
    },
    async onDelete() {
      if (!this.formId || !this.isEdit) {
        return;
      }
      const name = get(this.group, 'label') || get(this.group, 'group_key');
      const confirmed = await this.$aiq.confirm(
        this.$t(`${this.baseTranslationPath}.delete_group_title`),
        this.$t(`${this.baseTranslationPath}.delete_group_confirm`, { name }),
      ).catch(() => false);
      if (!confirmed) {
        return;
      }
      this.deleting = true;
      try {
        await this.$store.dispatch('agentForms/deleteFormGroup', {
          formId: this.formId,
          groupId: this.group.id,
        });
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.group_deleted`));
        this.$emit('deleted', this.group.id);
        this.close();
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.group_delete_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.g-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.g-row {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    font-weight: 600;
  }
}

.g-hint {
  font-size: 11px;
  color: $aiq-color-text-secondary;
}

.btn-check {
  margin-right: 6px;
}

.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.actions-right {
  display: flex;
  gap: 8px;
}
</style>
